export const ServerErrors = {
  GENERIC_ERROR: 'generic_error',
  LOGGED_OUT: 'logged_out',

  // auth errors
  // (these match the possible values of the `code` property on the error response from firebase)
  WEAK_PASSWORD: 'auth/weak-password',
  INVALID_PASSWORD: 'auth/wrong-password',
  INVALID_EMAIL: 'auth/invalid-email',
  USER_NOT_FOUND: 'auth/user-not-found',
  EMAIL_ALREADY_IN_USE: 'auth/email-already-exists',
  REQUIRES_RECENT_LOGIN: 'auth/requires-recent-login',
  // these ones are ours, not from firebase
  USERNAME_UNAVAILABLE: 'username-unavailable',
  USERNAME_INVALID: 'username-invalid',
  INVALID_TOKEN: 'invalid-token',
};

export const STATS_DIALOG_DELAY = 2000;

export type PuzzleStoreBase = {
  puzzleId: number;
  timerUpdatingFn: NodeJS.Timeout | undefined;
  time: number;
  isPaused: boolean;
  isSoftPaused: boolean; // when dialogs are open. stops timer but doesn't show pause cover.
  instructionsAreOpen: boolean;
  revealConfirmationIsOpen: boolean;
  statsDialogIsOpen: boolean;
  solved: boolean;
  revealed: boolean;
  completed?: boolean;
};

export const PuzzleStoreBaseDefaults = {
  puzzleId: 0,
  timerUpdatingFn: undefined,
  time: 0,
  isPaused: false,
  isSoftPaused: false,
  instructionsAreOpen: false,
  revealConfirmationIsOpen: false,
  statsDialogIsOpen: false,
  solved: false,
  revealed: false,
};

// for indexing data structures
export enum PuzzleNames {
  Arithmeglyphs = 'arithmeglyphs',
  Megarubigram = 'megarubigram',
  Morphology = 'morphology',
  Pathword = 'pathword',
  Pathword5x5 = 'pathword5x5',
  Rubigram = 'rubigram',
  Triword = 'triword',
  Truthsorting = 'truthsorting',
  Ultrarubigram = 'ultrarubigram',
  VaryClove = 'varyClove',
};

// for UI copy. indexable via the enum above.
export const PuzzleNamesForUI = {
  arithmeglyphs: 'Arithmeglyphs',
  megarubigram: 'Megarubigram',
  morphology: 'Morphology',
  pathword: 'Pathword',
  pathword5x5: 'Pathword 5x5',
  rubigram: 'Rubigram',
  triword: 'Triword',
  truthsorting: 'Truthsorting',
  ultrarubigram: 'Ultrarubigram',
  varyClove: 'Vary Clove',
};

export const DESKTOP_SHARE_TAG = '_desktop';
export const MOBILE_SHARE_TAG = '_mobile';

// sb for share button, d for desktop, m for mobile, then last is puzzle identifer.
// the native apps will use sb_a[puzzlecode] and sb_i[puzzlecode],
// with a for android and i for ios
export const PuzzleShareCodes = {
  [PuzzleNames.Arithmeglyphs + DESKTOP_SHARE_TAG]: 'sb_dAr',
  [PuzzleNames.Megarubigram + DESKTOP_SHARE_TAG]: 'sb_dMe',
  [PuzzleNames.Morphology + DESKTOP_SHARE_TAG]: 'sb_dMo',
  [PuzzleNames.Pathword + DESKTOP_SHARE_TAG]: 'sb_dPa',
  [PuzzleNames.Pathword5x5 + DESKTOP_SHARE_TAG]: 'sb_dPa5',
  [PuzzleNames.Rubigram + DESKTOP_SHARE_TAG]: 'sb_dRu',
  [PuzzleNames.Triword + DESKTOP_SHARE_TAG]: 'sb_dTr',
  [PuzzleNames.Truthsorting + DESKTOP_SHARE_TAG]: 'sb_dTs',
  [PuzzleNames.Ultrarubigram + DESKTOP_SHARE_TAG]: 'sb_dUl',
  [PuzzleNames.VaryClove + DESKTOP_SHARE_TAG]: 'sb_dVC',
  [PuzzleNames.Arithmeglyphs + MOBILE_SHARE_TAG]: 'sb_mAr',
  [PuzzleNames.Megarubigram + MOBILE_SHARE_TAG]: 'sb_mMe',
  [PuzzleNames.Morphology + MOBILE_SHARE_TAG]: 'sb_mMo',
  [PuzzleNames.Pathword + MOBILE_SHARE_TAG]: 'sb_mPa',
  [PuzzleNames.Pathword5x5 + MOBILE_SHARE_TAG]: 'sb_mPa5',
  [PuzzleNames.Rubigram + MOBILE_SHARE_TAG]: 'sb_mRu',
  [PuzzleNames.Triword + MOBILE_SHARE_TAG]: 'sb_mTr',
  [PuzzleNames.Truthsorting + MOBILE_SHARE_TAG]: 'sb_mTs',
  [PuzzleNames.Ultrarubigram + MOBILE_SHARE_TAG]: 'sb_mUl',
  [PuzzleNames.VaryClove + MOBILE_SHARE_TAG]: 'sb_mVC',
};

export type HoroscopesData = {
  [sign: string]: string;
};

export type ArithmeglyphsData = {
  line1_opSeq: string;
  line1_numSeq: string;
  line2_opSeq: string;
  line2_numSeq: string;
  line3_numSeq: string;
  puzzleId: number;
};

export type MorphologyClue = {
  solution: string;
  clue: string;
  morph: string | null;
};
export type MorphologyData = {
  author: string;
  clues: MorphologyClue[];
  puzzleId: number;
  themeless?: boolean;
  cryptic?: boolean;
};

export type RubigramData = {
  scrambledGrid: string[][][];
  solutionGrid: string[][][];
  puzzleId: number;
};

export type MegarubigramData = {
  id: string;
  grid: string[]; // solvedGrid, this should have been called
  scrambledGrid: string[];
  puzzleId: number;
};

export type UltrarubigramData = {
  grid: string[]; // the solved 24x24, one string per row
  scrambledGrid: string[];
  puzzleId: number;
};

/** Where one piece of the Ultrarubigram has been turned and flipped to. */
export type UltrarubigramPieceOrientation = {
  rot: number;
  flipH: boolean;
  flipV: boolean;
};

/** A mid-puzzle save: the whole puzzle's turn plus every piece's orientation. The letters come from the puzzle data. */
export type UltrarubigramPartialState = {
  rot: number;
  grids: (UltrarubigramPieceOrientation & {
    quadrants: (UltrarubigramPieceOrientation & {
      subgrids: UltrarubigramPieceOrientation[];
    })[];
  })[];
};

export type Position = [number, number];
export type PathwordData = {
  cluetext: string[];
  solutionPath: Position[];
  solutionWords: string[];
  vowelMatrix: number[][];
  puzzleId: number;
  hintLetters: number[];
};

export type TriwordData = {
  clues: string[];
  solution: string;
  obscureSolutions: string[];
  positionLifeline: string;
  lettersFor5050: string;
  puzzleId: number;
};

export enum TruthsortingColors {
  Red = 'red',
  Blue = 'blue',
  Green = 'green',
  Yellow = 'yellow',
  Pink = 'pink',
  Orange = 'orange',
  Purple = 'purple',
  Cyan = 'cyan',
};

enum TruthsortingRequirements {
  NthStatement = 'NthStatement',
  FollowedByVeracity = 'FollowedByVeracity',
  PrecededByVeracity = 'PrecededByVeracity',
  FollowedByColor = 'FollowedByColor',
  PrecededByColor = 'PrecededByColor',
  ColorVeracityAssertion = 'ColorVeracityAssertion',
  PositionalParity = 'PositionalParity',
  SurroundedBy = 'SurroundedBy',
  ColorPosition = 'ColorPosition',
  DistanceFromColor = 'DistanceFromColor',
  BothVeracityAssertion = 'BothVeracityAssertion',
  XORVeracityAssertion = 'XORVeracityAssertion',
  ColorOrder = 'ColorOrder',
  BetweenColors = 'BetweenColors',
  SelfIsCloserToColor = 'SelfIsCloserToColor',
  SelfIsFirstOrLast = 'SelfIsFirstOrLast',
  ColorIsFirstOrLast = 'ColorIsFirstOrLast',
  SelfAdjacency = 'SelfAdjacency',
  ColorAdjacency = 'ColorAdjacency',
  XNORVeracityAssertion = 'XNORVeracityAssertion',
  NegatedNthStatement = 'NegatedNthStatement',
  NegatedFollowedByColor = 'NegatedFollowedByColor',
  NegatedPrecededByColor = 'NegatedPrecededByColor',
  NegatedSurroundedBy = 'NegatedSurroundedBy',
  NegatedColorPosition = 'NegatedColorPosition',
  NegatedColorIsFirstOrLast = 'NegatedColorIsFirstOrLast',
  NegatedSelfIsFirstOrLast = 'NegatedSelfIsFirstOrLast',
  NegatedSelfAdjacency = 'NegatedSelfAdjacency'
};

export type TruthsortingStatement = {
  color: TruthsortingColors;
  requirement: {
    type: TruthsortingRequirements;
    params: any;
  };
  text: string;
  simpleText: string;
  colorblindText: string;
};

export type TruthsortingData = {
  slots: boolean[];
  statements: TruthsortingStatement[];
  solution: number[];
  puzzleId: number;
};

export type VaryCloveData = {
  prompt: string;
  cluePhrase: string;
  numAlteredLetters: number;
  solution: string;
  puzzleId: number;
};

export type ContentData = {
  puzzleData: PuzzleData;
  featureData: FeatureData;
};

export type PuzzleData = {
  arithmeglyphs: ArithmeglyphsData;
  megarubigram: MegarubigramData;
  morphology: MorphologyData;
  pathword: PathwordData;
  pathword5x5: PathwordData;
  rubigram: RubigramData;
  triword: TriwordData;
 // temporary while truthsortingV1 is being deprecated; rename to just `truthsorting` pending
  truthsortingV2: TruthsortingData;
  ultrarubigram: UltrarubigramData;
  varyClove: VaryCloveData;
};

export type FeatureData = {
  weeklyWisdom: string;
  horoscopes: HoroscopesData;
};

// matches type in types.ts on backend
export type PuzzleState = {
  puzzleId: number;
  revealed: boolean;
  solved: boolean;
  time: number;
  completed?: boolean;
  partialState?: any;
};

export type UpdateProgressBody = {
  authToken: string;
  puzzleName: PuzzleNames;
  puzzleState: PuzzleState;
};

export type UpdateSettingsBody = {
  authToken: string;
  newSettings: {
    hapticFeedbackEnabled?: boolean;
    pathwordDefaultHints?: number;
    pathword5x5DefaultHints?: number;
  };
};

export type LoadUserResponse = {
  date: string;
  puzzlesProgress: PuzzlesProgress;
  settings: any;
  stats: any;
};

export type UpdateProgressResponse = {
  // hard to type. it returns stats...
};

export type BasePuzzleProgress = {
  time: number;
  solved: boolean;
  revealed: boolean;
};

export type PuzzlesProgress = {
  [PuzzleNames.Arithmeglyphs]?: BasePuzzleProgress & {
    completed: boolean;
    partialState: {
      dataByLine: {
        guesses: (string | null)[];
        revealedCells: boolean[];
      }[];
      timeOfLastUpdate: string;
    } | null;
  },
  [PuzzleNames.Morphology]?: BasePuzzleProgress & {
    completed: boolean;
    partialState: {
      guesses: (string | null)[][],
      revealedCells: boolean[][],
      timeOfLastUpdate: string;
    } | null;
  },
  [PuzzleNames.Megarubigram]?: BasePuzzleProgress & {
    partialState: {
      degreesRotated: number;
      grids: any[]; // sigh
    } | null;
  },
  [PuzzleNames.Pathword]?: BasePuzzleProgress,
  [PuzzleNames.Pathword5x5]?: BasePuzzleProgress,
  [PuzzleNames.Rubigram]?: BasePuzzleProgress,
  [PuzzleNames.Triword]?: BasePuzzleProgress & {
    partialState: {
      usedPositionLifeline: boolean;
      usedLetterLifeline: boolean;
      used5050Lifeline: boolean;
    } | null;
  },
  [PuzzleNames.Truthsorting]?: BasePuzzleProgress & {
    partialState: {
      statementOrder: number[];
      revealedSlots: number[];
    } | null;
  },
  [PuzzleNames.Ultrarubigram]?: BasePuzzleProgress & {
    partialState: UltrarubigramPartialState | null;
  },
  [PuzzleNames.VaryClove]?: BasePuzzleProgress,
};